/**
 * Main App Component
 * Integrates Map and ChatInterface components
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Map from './components/Map';
import ChatInterface from './components/ChatInterface';
import AnalyticsPanel from './components/AnalyticsPanel';
import DatabricksPanel from './components/DatabricksPanel';
import { getContractsByTractNumbers } from './services/contractService';
import { useAuth } from './components/AuthProvider';
import { getTenantConfig } from './config/tenantConfig';
import './styles/App.css';

// Get tenant config
const tenantConfig = getTenantConfig();

function App({ user: propUser }) {
  const { user: authUser, logout } = useAuth();
  const [queryResults, setQueryResults] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureContracts, setFeatureContracts] = useState([]);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [panelPosition, setPanelPosition] = useState({ x: 20, y: 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [databricksPanelOpen, setDatabricksPanelOpen] = useState(false);
  const [lastQueryText, setLastQueryText] = useState('');

  // Ref to ChatInterface for sending queries programmatically
  const chatRef = useRef(null);

  // Custom symbology overrides (user-driven color changes)
  const [symbologyOverrides, setSymbologyOverrides] = useState({
    tractTypes: {},   // e.g., { 'F': '#ff0000' }
    wellStatuses: {}  // e.g., { 'PRODUCING': '#00ff00' }
  });

  // Handle query results from chat interface
  const handleQueryResults = useCallback((results) => {
    setQueryResults(results);
    setError(null);
    // Track query text for saving analyses
    if (results?.query) {
      setLastQueryText(results.query);
    }
  }, []);

  // Handle analytics data
  const handleAnalytics = useCallback((data) => {
    console.log('App received analytics data:', data);
    setAnalyticsData(data);
  }, []);

  // Clear analytics data
  const handleClearAnalytics = useCallback(() => {
    setAnalyticsData(null);
  }, []);

  // Handle symbology change requests from chat
  const handleSymbologyChange = useCallback((change) => {
    console.log('🎨 Symbology change:', change);

    if (change.target === 'wellStatus') {
      setSymbologyOverrides(prev => ({
        ...prev,
        wellStatuses: {
          ...prev.wellStatuses,
          [change.value]: change.color
        }
      }));
    } else if (change.target === 'tractType') {
      setSymbologyOverrides(prev => ({
        ...prev,
        tractTypes: {
          ...prev.tractTypes,
          [change.value]: change.color
        }
      }));
    } else if (change.target === 'allWells') {
      // Apply color to all well statuses
      const allWellStatuses = ['PRODUCING', 'DUC', 'DRILLING', 'PERMITTED', 'COMPLETED', 'INACTIVE PRODUCER', 'SHUT-IN', 'P & A'];
      const newOverrides = {};
      allWellStatuses.forEach(status => {
        newOverrides[status] = change.color;
      });
      setSymbologyOverrides(prev => ({
        ...prev,
        wellStatuses: newOverrides
      }));
    } else if (change.target === 'allTracts') {
      // Apply color to all tract types
      const allTractTypes = ['F', 'O', 'N', 'U', 'Q', 'V'];
      const newOverrides = {};
      allTractTypes.forEach(type => {
        newOverrides[type] = change.color;
      });
      setSymbologyOverrides(prev => ({
        ...prev,
        tractTypes: newOverrides
      }));
    }
  }, []);

  // Handle analytics filter clicks (e.g., clicking on a county/operator in analytics table)
  const handleAnalyticsFilterClick = useCallback((filterType, value) => {
    console.log('📊 Analytics filter clicked:', filterType, value);

    // Build a natural language filter query based on the filter type
    let filterQuery = '';
    switch (filterType) {
      case 'county':
        filterQuery = `Filter to ${value} County only`;
        break;
      case 'operator':
        filterQuery = `Filter to operator ${value}`;
        break;
      case 'status':
        filterQuery = `Filter to ${value} status`;
        break;
      case 'formation':
        filterQuery = `Filter to ${value} formation`;
        break;
      default:
        filterQuery = `Filter by ${filterType}: ${value}`;
    }

    // Send the query to ChatInterface via ref
    if (chatRef.current) {
      chatRef.current.sendQuery(filterQuery);
    }
  }, []);

  // Handle errors
  const handleError = useCallback((err) => {
    setError(err.message);
    console.error('Application error:', err);
  }, []);

  // Handle feature click on map - MUST be memoized to prevent map remount
  const handleFeatureClick = useCallback((feature, layerKey) => {
    setSelectedFeature({ feature, layerKey });
    setFeatureContracts([]); // Reset contracts when new feature selected
  }, []);

  // Fetch contracts when a feature is selected
  useEffect(() => {
    async function fetchFeatureContracts() {
      if (!selectedFeature) {
        setFeatureContracts([]);
        return;
      }

      console.log('🔍 Selected feature:', selectedFeature);
      console.log('🔍 Feature properties:', selectedFeature.feature.properties);
      console.log('🔍 Feature attributes:', selectedFeature.feature.attributes);

      // Check both properties and attributes (ArcGIS uses attributes)
      const props = selectedFeature.feature.properties || selectedFeature.feature.attributes || {};

      const tractNumber = props.TRACT_NUMBER ||
                         props.TractNumber ||
                         props.tract_number;

      console.log('🔍 Extracted tract number:', tractNumber);

      if (tractNumber) {
        try {
          console.log('🔍 Fetching contracts for tract:', tractNumber);
          const contracts = await getContractsByTractNumbers([tractNumber]);
          console.log('✅ Contracts fetched:', contracts);
          setFeatureContracts(contracts);
        } catch (error) {
          console.error('❌ Failed to fetch contracts for feature:', error);
          setFeatureContracts([]);
        }
      } else {
        console.warn('⚠️ No tract number found in feature properties');
        setFeatureContracts([]);
      }
    }

    fetchFeatureContracts();
  }, [selectedFeature]);

  // Drag handlers for feature panel
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - panelPosition.x,
      y: e.clientY - panelPosition.y
    });
  }, [panelPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPanelPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="tenant-logo" style={{
            width: '48px',
            height: '48px',
            background: tenantConfig.logo.gradient,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: tenantConfig.logo.text.length > 2 ? '14px' : '20px',
            fontWeight: 'bold',
            color: 'white',
          }}>
            {tenantConfig.logo.text}
          </div>
          <div className="header-text">
            <h1>{tenantConfig.name}</h1>
            <p>{tenantConfig.tagline}</p>
          </div>
        </div>
        <div className="user-profile">
          <span className="user-email">{authUser?.email || authUser?.username || 'User'}</span>
          <button className="logout-btn" onClick={logout}>Sign Out</button>
        </div>
      </header>

      <div className="app-content">
        <div className="map-section">
          <Map
            queryResults={queryResults}
            onFeatureClick={handleFeatureClick}
            symbologyOverrides={symbologyOverrides}
          />
          <AnalyticsPanel
            analyticsData={analyticsData}
            onFilterClick={handleAnalyticsFilterClick}
          />
        </div>

        <div className="chat-section">
          <ChatInterface
            ref={chatRef}
            onQueryResults={handleQueryResults}
            onAnalytics={handleAnalytics}
            onClearAnalytics={handleClearAnalytics}
            onError={handleError}
            onSymbologyChange={handleSymbologyChange}
            currentResults={queryResults?.features}
            queryText={lastQueryText}
            analyticsData={analyticsData}
          />
        </div>
      </div>

      {/* Error notification */}
      {error && (
        <div className="error-notification">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Selected feature panel */}
      {selectedFeature && (
        <div
          className="feature-panel"
          style={{
            left: `${panelPosition.x}px`,
            top: `${panelPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'auto'
          }}
        >
          <div
            className="feature-panel-header"
            onMouseDown={handleMouseDown}
            style={{ cursor: 'grab' }}
          >
            <h3>Tract Details</h3>
            <button onClick={() => setSelectedFeature(null)}>✕</button>
          </div>
          <div className="feature-panel-content">
            <p><strong>Layer:</strong> {selectedFeature.layerKey}</p>
            <hr />
            {(() => {
              const props = selectedFeature.feature.properties || selectedFeature.feature.attributes || {};
              return Object.entries(props).map(([key, value]) => (
                <p key={key}>
                  <strong>{key}:</strong> {value?.toString() || 'N/A'}
                </p>
              ));
            })()}

            {/* Contract Data Section */}
            {featureContracts.length > 0 && (
              <>
                <hr style={{ margin: '1rem 0', borderColor: '#4caf50' }} />
                <h4 style={{ color: '#4caf50', marginBottom: '0.5rem' }}>
                  📋 Contracts ({featureContracts.length})
                </h4>
                {featureContracts.map((contract, idx) => (
                  <div key={idx} style={{
                    background: '#e8f5e9',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <p><strong>Contract ID:</strong> {contract.contract_id}</p>
                    <p><strong>Operator:</strong> {contract.operator_name}</p>
                    <p><strong>Type:</strong> {contract.contract_type}</p>
                    <p><strong>Status:</strong> {contract.lease_status}</p>
                    <p><strong>Start Date:</strong> {contract.lease_start_date}</p>
                    <p><strong>End Date:</strong> {contract.lease_end_date}</p>
                    <p><strong>Monthly Revenue:</strong> ${contract.monthly_revenue?.toLocaleString() || 0}</p>
                    <p><strong>Royalty %:</strong> {(contract.royalty_percentage * 100).toFixed(1)}%</p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Databricks Analytics Panel - only shows when analytics data exists */}
      <DatabricksPanel
        isOpen={databricksPanelOpen}
        onToggle={() => setDatabricksPanelOpen(!databricksPanelOpen)}
        selectedWell={selectedFeature}
        analyticsData={analyticsData}
        queryResults={queryResults}
      />
    </div>
  );
}

export default App;
